import { AxiosResponse } from 'axios';
import MobileDetect from 'mobile-detect';
import PropTypes from 'prop-types';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import UAParser from 'ua-parser-js';

import { ConfigBase } from '../../types/config';
import { AppLookupValuesDTO } from '../../types/dto/AppLookupValuesDTO';

import { Location } from 'history';
import { GeolocationsDTO } from '../../types/dto/GeolocationsDTO';
import { PMPoliciesDTO } from '../../types/dto/PMPoliciesDTO';
import { PMPolicyConsentsDTO } from '../../types/dto/PMPolicyConsentsDTO';
import { PolicyConsentsDTO } from '../../types/dto/PolicyConsentsDTO';
import { PolicyDTO } from '../../types/dto/PolicyDTO';
import ApiHelper from '../ApiHelper';
import { AuthState } from '../auth/Types';
import * as Actions from './Actions';
import { AppState } from './Types';

type RootSubState = {
    authState: AuthState,
    appState: AppState,
};

type CommonAppThunkAction<R> = ThunkAction<R, RootSubState, undefined, Action>;
type CommonAppThunkDispatch = ThunkDispatch<RootSubState, undefined, Action>;

export type ServiceActions = {
    ping: () => Promise<void>,
    logClientInfo: () => Promise<void>,
    setPolicyConsents: (selectedPolicyConsent: PolicyConsentsDTO) => Promise<void>,
    setPMPolicyConsents: (consents: PMPolicyConsentsDTO) => Promise<void>,
    menuGoBackUrl: (location: Location<any>) => Promise<void>,
    loadLookupValues: () => Promise<AppLookupValuesDTO>,
    getGeolocations: () => Promise<GeolocationsDTO[]>,
    loadPolicies: () => Promise<PolicyDTO>,
    loadPMPolicies: () => Promise<PMPoliciesDTO>,
};

export const ServiceActionPropTypes = PropTypes.shape({
    ping: PropTypes.func.isRequired,
    logClientInfo: PropTypes.func.isRequired,
    setPolicyConsents: PropTypes.func.isRequired,
    setPMPolicyConsents: PropTypes.func.isRequired,
    menuGoBackUrl: PropTypes.func.isRequired,
    loadLookupValues: PropTypes.func.isRequired,
    getGeolocations: PropTypes.func.isRequired,
    loadPolicies: PropTypes.func.isRequired,
    loadPMPolicies: PropTypes.func.isRequired,
});

export const createServiceActions = (config: ConfigBase) => {
    const apiHelper = ApiHelper(config);

    const ping = (): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.pingRequest());
                await dispatch(apiHelper.get(`/api/${config.ResourceCode}/app/ping`, null));
                dispatch(Actions.pingSuccess());
                return;
            } catch (error) {
                dispatch(Actions.pingFailure());
                throw error;
            }
        };
    };

    const logClientInfo = (): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                const md = new MobileDetect(window.navigator.userAgent);
                const ua = new UAParser().getResult();
                const deviceType = (() => {
                    if (md.phone()) { return 'Phone'; } else if (md.tablet()) { return 'Tablet'; } else if (md.mobile()) { return 'Mobile (Other)'; }
                    return 'Unknown';
                })();
                const deviceInfo = ua.device.vendor || ua.device.model ? (ua.device.vendor ? ua.device.vendor + ' ' : '') + (ua.device.model ? ua.device.model : '') : 'Unknown';
                const os = `${ua.os.name} ${ua.os.version}`;
                const browser = `${ua.browser.name} ${ua.browser.version}`;
                const engine = `${ua.engine.name} ${ua.engine.version}`;
                dispatch(Actions.logClientInfoRequest());
                await dispatch(apiHelper.get(`/api/${config.ResourceCode}/app/client-info?deviceType=${deviceType}&deviceInfo=${deviceInfo}&os=${os}&browser=${browser}&engine=${engine}`, null));
                dispatch(Actions.logClientInfoSuccess());
            } catch (error) {
                dispatch(Actions.logClientInfoFailure());
                throw error;
            }
        };
    };

    const menuGoBackUrl = (location: Location<any>): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            dispatch(Actions.menuGoBackUrl(location));
            return;
        };
    };

    const setPolicyConsents = (selectedPolicyConsent: PolicyConsentsDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.setPolicyConsentsRequest());
                await dispatch(apiHelper.post(`/api/${config.ResourceCode}/common/policyConsents`, selectedPolicyConsent));
                dispatch(Actions.setPolicyConsentsSuccess());
                return;
            } catch (error) {
                dispatch(Actions.setPolicyConsentsFailure());
                throw error;
            }
        };
    };

    const setPMPolicyConsents = (consents: PMPolicyConsentsDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            await dispatch(apiHelper.post(`/api/${config.ResourceCode}/common/pmPolicyConsents`, consents));
        };
    };

    const loadLookupValues = (): CommonAppThunkAction<Promise<AppLookupValuesDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.loadLookupValuesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/app/lookupvalues/`, null));
                const response = axiosResponse.data as AppLookupValuesDTO;
                dispatch(Actions.loadLookupValuesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.loadLookupValuesFailure());
                throw error;
            }
        };
    };

    const getGeolocations = (): CommonAppThunkAction<Promise<GeolocationsDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getGeolocationsRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/app/getgeolocations/`, null));
                const response = axiosResponse.data as GeolocationsDTO[];
                dispatch(Actions.getGeolocationsSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getGeolocationsFailure());
                throw error;
            }
        };
    };

    const loadPolicies = (): CommonAppThunkAction<Promise<PolicyDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.loadPoliciesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/app/getpolicieslines/`, null));
                const response = axiosResponse.data as PolicyDTO;
                dispatch(Actions.loadPoliciesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.loadPoliciesFailure());
                throw error;
            }
        };
    };

    const loadPMPolicies = (): CommonAppThunkAction<Promise<PMPoliciesDTO>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.loadPMPoliciesRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/app/getpmpolicies/`, null));
                const response = axiosResponse.data as PMPoliciesDTO;
                dispatch(Actions.loadPMPoliciesSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.loadPMPoliciesFailure());
                throw error;
            }
        };
    };

    return {
        ping,
        logClientInfo,
        setPolicyConsents,
        setPMPolicyConsents,
        menuGoBackUrl,
        loadLookupValues,
        getGeolocations,
        loadPolicies,
        loadPMPolicies,
    };
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: CommonAppThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        ping: () => dispatch(services.ping()),
        logClientInfo: () => dispatch(services.logClientInfo()),
        setPolicyConsents: (selectedPolicyConsent: PolicyConsentsDTO) => dispatch(services.setPolicyConsents(selectedPolicyConsent)),
        setPMPolicyConsents: (consents: PMPolicyConsentsDTO) => dispatch(services.setPMPolicyConsents(consents)),
        menuGoBackUrl: (location: Location<any>) => dispatch(services.menuGoBackUrl(location)),
        loadLookupValues: () => dispatch(services.loadLookupValues()),
        getGeolocations: () => dispatch(services.getGeolocations()),
        loadPolicies: () => dispatch(services.loadPolicies()),
        loadPMPolicies: () => dispatch(services.loadPMPolicies()),
    };
};
